import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ComponentProps } from "react";

import { cn } from "@/lib/utils/cn";

export const cardVariants = cva(
  "rounded-card border border-[var(--card-surface-border)] bg-[var(--surface-card)] text-foreground shadow-[var(--card-surface-shadow)] transition-[transform,box-shadow,border-color,background-color] duration-base ease-standard",
  {
    variants: {
      variant: {
        default: "",
        elevated: "bg-[var(--surface-card-elevated)] shadow-[var(--card-surface-shadow-hover)]",
        outlined: "border-[var(--card-surface-border-strong)] bg-[var(--surface-card)] shadow-none",
        flat: "bg-[var(--surface-card)] shadow-none",
      },
      interactive: {
        true: "cursor-pointer hover:-translate-y-px hover:border-[var(--card-surface-border-strong)] hover:bg-[var(--surface-card-hover)] hover:shadow-[var(--card-surface-shadow-hover)] active:translate-y-0",
        false: "",
      },
      padding: {
        none: "p-0",
        sm: "p-[var(--density-panel-padding-compact)]",
        md: "p-[var(--density-panel-padding)]",
        lg: "p-ds-6",
      },
    },
    defaultVariants: { variant: "default", interactive: false, padding: "md" },
  },
);

export type CardProps = ComponentProps<"div"> &
  VariantProps<typeof cardVariants> & {
    /** Backward-compatible stronger border. */
    emphasis?: boolean;
  };

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, variant, interactive, padding, emphasis, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(cardVariants({ variant, interactive, padding }), emphasis && "border-[var(--card-surface-border-strong)]", className)}
      {...props}
    />
  );
});

export const CardHeader = forwardRef<HTMLDivElement, ComponentProps<"div">>(function CardHeader({ className, ...props }, ref) {
  return <div ref={ref} className={cn("flex flex-col gap-ds-1 border-b border-[var(--card-surface-border)] bg-[var(--surface-card-elevated)] px-ds-5 py-ds-4 sm:flex-row sm:items-start sm:justify-between", className)} {...props} />;
});

export const CardTitle = forwardRef<HTMLHeadingElement, ComponentProps<"h2">>(function CardTitle({ className, ...props }, ref) {
  return <h2 ref={ref} className={cn("text-title text-foreground", className)} {...props} />;
});

export const CardDescription = forwardRef<HTMLParagraphElement, ComponentProps<"p">>(function CardDescription({ className, ...props }, ref) {
  return <p ref={ref} className={cn("text-caption text-muted", className)} {...props} />;
});

export const CardContent = forwardRef<HTMLDivElement, ComponentProps<"div">>(function CardContent({ className, ...props }, ref) {
  return <div ref={ref} className={cn("p-[var(--density-panel-padding)] pt-ds-4", className)} {...props} />;
});

export const CardFooter = forwardRef<HTMLDivElement, ComponentProps<"div">>(function CardFooter({ className, ...props }, ref) {
  return <div ref={ref} className={cn("flex items-center gap-ds-2 border-t border-[var(--card-surface-border)] bg-[var(--surface-card-elevated)] px-ds-5 py-ds-4", className)} {...props} />;
});

export const Panel = forwardRef<HTMLDivElement, ComponentProps<"section">>(function Panel({ className, ...props }, ref) {
  return <section ref={ref} className={cn("rounded-card border border-[var(--card-surface-border)] bg-[var(--surface-card)] p-[var(--density-panel-padding)] text-foreground shadow-[var(--card-surface-shadow)]", className)} {...props} />;
});

export const InsetRegion = forwardRef<HTMLDivElement, ComponentProps<"div">>(function InsetRegion({ className, ...props }, ref) {
  return <div ref={ref} className={cn("rounded-control border border-[var(--card-surface-border)] bg-[var(--surface-card-inset)] p-[var(--density-panel-padding-compact)] text-foreground shadow-none", className)} {...props} />;
});
